<script lang="ts">
	import { onDestroy } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getZoneDuration,
		irrigationZoneColors,
		ZoneTimer,
	} from 'src/helpers/xt1_helpers/irrigation/irrigation_helpers';
	import { capFirstChar, to_hh_mm_ss } from 'src/helpers/utilities';
	import { translateTemplateTitle } from 'src/helpers/xt1_helpers/template_helpers';
	//Design system
	import { Tooltip } from '@Shelly/shelly-design-system/index';
	//Components
	import IrrigationConfirmModal from './IrrigationConfirmModal.svelte';
	import IrrigationControllButton from './IrrigationControllButton.svelte';
	import IrrigationZoneCard from './IrrigationZoneCard.svelte';
	//Types
	import type { Sequence, VCInterfaceStatus } from 'src/types/VirtualComponents';
	import type { ButtonTimer } from 'src/helpers/xt1_helpers/irrigation/irrigation_helpers';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';

	export let device: Device,
		zone: VCInterfaceStatus,
		activeZones: Sequence['zones'] | null = null,
		activeZone: number | null = null,
		showOnlyButton = false;

	const onTimerUpdate = (t: ButtonTimer) => {
		buttonTimer = t;
	};
	const handleToggle = async () => {
		if (waitForResponse) return;
		if (activeZone !== null && !(await confirmModal.confirm())) return;
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'boolean.set', {
			id: zone.id,
			value: !zone.event,
		});
		if (!res?.isok) {
			onSettingsResponse(res, device);
		}
		waitForResponse = false;
	};

	let index = zone.id - 200;
	let waitForResponse = false;
	let confirmModal: IrrigationConfirmModal;
	let buttonTimer: ButtonTimer = { time: 0, percentage: 100 };
	let zoneTimer: ZoneTimer;

	$: {
		if (!zoneTimer) zoneTimer = new ZoneTimer(onTimerUpdate);
		if (zone?.timer?.started_at && zone.event) {
			if (!zoneTimer.isRunning) zoneTimer.start(zone.timer);
		} else if (zoneTimer.isRunning) {
			zoneTimer.reset();
		}
	}
	$: inactive = activeZones && !activeZones.includes(index);
	$: scheduled = !zone?.event && activeZones && activeZones.includes(index);
	$: done =
		scheduled &&
		(activeZones && activeZone != null ? activeZones.indexOf(index) < activeZone : false);

	onDestroy(() => zoneTimer?.stop());
</script>

<!-- Zone Cards - Each zone gets its own card -->
{#if showOnlyButton}
	<IrrigationControllButton
		isActive={zone?.event}
		{done}
		{buttonTimer}
		disabled={true}
		fillNumber={index + 1}
		color={irrigationZoneColors[index]}
	/>
{:else}
	<IrrigationZoneCard {index} {inactive}>
		<svelte:fragment slot="main">
			<h3>{translateTemplateTitle(zone?.value)}</h3>
			{#if inactive}
				<span class="sequence-duration">
					<i class="icon fas fa-ban"></i>
					<span class="title">{$format('label_inactive', { default: 'Inactive' })}</span>
				</span>
			{:else if done}
				<span class="sequence-duration">
					<i class="icon fas fa-leaf"></i>
					<span class="title">{$format('button_done', { default: 'Done' })}</span>
				</span>
			{:else}
				<div class="sequence-duration">
					{#if zone?.event}
						{@const zoneDuration = getZoneDuration(zone)}
						<Tooltip placement="top" title={zoneDuration.additionalTitle}>
							<i class="icon far fa-droplet"></i>

							<span class="title">
								{$format('label_watering', { default: 'Watering' })}
							</span>
							<span class="timer active">
								{buttonTimer?.time > 0 ? to_hh_mm_ss(buttonTimer.time) : ''}
							</span>
						</Tooltip>
					{:else}
						{#if scheduled}
							<i class="icon far fa-calendar-clock"></i>
							<span class="title"
								>{$format('label_scheduled', { default: 'Scheduled' })}</span
							>
						{/if}
						{@const zoneDuration = getZoneDuration(zone)}
						{#if zoneDuration.additionalDuration === 0}
							<i class="icon fas fa-timer"></i>
							<span class="timer{scheduled ? ' scheduled' : ''}">{zoneDuration.title}</span
							>
						{:else}
							<Tooltip placement="top" title={zoneDuration.additionalTitle}>
								{#if !activeZones}
									<i class="icon fal fa-cloud-sun-rain"></i>
								{/if}
								{#if zoneDuration.duration === 0}
									{capFirstChar($format('disabled', { default: 'disabled' }))}
								{:else}
									<span class="timer{scheduled ? ' scheduled' : ''}"
										>{zoneDuration.title}</span
									>
								{/if}
							</Tooltip>
						{/if}
					{/if}
				</div>
			{/if}
		</svelte:fragment>
		<IrrigationControllButton
			slot="control"
			isActive={zone?.event}
			{done}
			{buttonTimer}
			color={irrigationZoneColors[index]}
			disabled={done || inactive || waitForResponse}
			on:click={handleToggle}
		/>
	</IrrigationZoneCard>
	<IrrigationConfirmModal bind:this={confirmModal} />
{/if}

<style>
	h3 {
		margin: 0;
		padding: 0;
		font-size: 16px;
		font-weight: 500;
		color: white;
	}
	.sequence-duration {
		margin-top: 4px;
		font-size: 12px;
	}

	.timer.active,
	.timer.scheduled {
		color: #9f9f9f;
	}
	.title,
	.icon {
		margin-right: 4px;
	}
	.title {
		display: inline;
	}
	:global(.zone-card .zone-info .tooltip-element) {
		display: inline-block !important;
	}
</style>
